// src/components/BugReports/statusUtils.js
//
// Shared status + type taxonomy for the Reports & Requests UI. The canonical
// lists come from `globalSettings/bugReportsConfig` (loaded inside the backend
// callable), but the UI needs a static fallback for labels/colors/icons when
// the config hasn't loaded yet.

import { Bug, Sparkles } from 'lucide-vue-next'

export const STATUS_OPTIONS = [
  { id: 'filed', label: 'Filed' },
  { id: 'in_progress', label: 'In Progress' },
  { id: 'complete', label: 'Complete' },
  { id: 'not_resolved', label: 'Not Resolved' },
]

const STATUS_LABELS = {
  filed: 'Filed',
  in_progress: 'In Progress',
  complete: 'Complete',
  not_resolved: 'Not Resolved',
}

const STATUS_PILL_CLASSES = {
  filed: 'bg-blue-100 text-blue-800',
  in_progress: 'bg-yellow-100 text-yellow-800',
  complete: 'bg-green-100 text-green-800',
  not_resolved: 'bg-red-100 text-red-800',
}

export function statusLabel(id) {
  return STATUS_LABELS[id] || id || 'Unknown'
}

export function statusPillClass(id) {
  return STATUS_PILL_CLASSES[id] || 'bg-gray-100 text-gray-700'
}

export const TERMINAL_STATUSES = new Set(['complete', 'not_resolved'])

// ── Report Types ──

export const TYPE_BUG = 'bug'
export const TYPE_FEATURE = 'feature'
export const DEFAULT_TYPE = TYPE_BUG

export const TYPE_OPTIONS = [
  { id: TYPE_BUG, label: 'Bug Report', short: 'Bug', icon: Bug },
  { id: TYPE_FEATURE, label: 'Feature Request', short: 'Feature', icon: Sparkles },
]

const TYPE_LABELS = {
  [TYPE_BUG]: 'Bug Report',
  [TYPE_FEATURE]: 'Feature Request',
}

const TYPE_SHORT_LABELS = {
  [TYPE_BUG]: 'Bug',
  [TYPE_FEATURE]: 'Feature',
}

const TYPE_BADGE_CLASSES = {
  [TYPE_BUG]: 'bg-red-50 text-red-700 ring-1 ring-red-200',
  [TYPE_FEATURE]: 'bg-purple-50 text-purple-700 ring-1 ring-purple-200',
}

const TYPE_ICONS = {
  [TYPE_BUG]: Bug,
  [TYPE_FEATURE]: Sparkles,
}

export function normalizeType(id) {
  return id === TYPE_FEATURE ? TYPE_FEATURE : TYPE_BUG
}

export function typeLabel(id) {
  return TYPE_LABELS[normalizeType(id)]
}

export function typeShortLabel(id) {
  return TYPE_SHORT_LABELS[normalizeType(id)]
}

export function typeBadgeClass(id) {
  return TYPE_BADGE_CLASSES[normalizeType(id)]
}

export function typeIcon(id) {
  return TYPE_ICONS[normalizeType(id)]
}

export function isFeatureRequest(report) {
  return normalizeType(report?.type) === TYPE_FEATURE
}

// ── Severity (bug-only) ──

export const DEFAULT_SEVERITY = 'medium'

export const SEVERITY_OPTIONS = [
  { id: 'low', label: 'Low', help: 'Cosmetic, edge case, or nice-to-have.' },
  {
    id: 'medium',
    label: 'Medium',
    help: 'Feature broken with a workaround, or a minor feature broken.',
  },
  { id: 'high', label: 'High', help: 'Major feature broken with no reasonable workaround.' },
  {
    id: 'critical',
    label: 'Critical',
    help: 'System unusable, data loss, security issue, or production outage.',
  },
]

const VALID_SEVERITIES = SEVERITY_OPTIONS.map(s => s.id)

const SEVERITY_LABELS = {
  critical: 'Critical',
  high: 'High',
  medium: 'Medium',
  low: 'Low',
}

const SEVERITY_BADGE_CLASSES = {
  critical: 'bg-red-100 text-red-800 ring-1 ring-red-300',
  high: 'bg-orange-100 text-orange-800 ring-1 ring-orange-200',
  medium: 'bg-amber-100 text-amber-800 ring-1 ring-amber-200',
  low: 'bg-gray-100 text-gray-700 ring-1 ring-gray-200',
}

export function severityLabel(id) {
  return SEVERITY_LABELS[id] || null
}

export function severityBadgeClass(id) {
  return SEVERITY_BADGE_CLASSES[id] || 'bg-gray-100 text-gray-700'
}

export function severityHelp(id) {
  return SEVERITY_OPTIONS.find(o => o.id === id)?.help || ''
}

export function normalizeSeverity(id) {
  if (!id) return null
  const v = String(id).trim().toLowerCase()
  return VALID_SEVERITIES.includes(v) ? v : null
}
